import { useRef, useState, useEffect } from 'react';

type UseObserveElementsByIdsOptions = {
  /**
   * an array of ids to observe
   */
  ids: string[];

  /**
   * IntersectionObserver.rootMargin
   */
  rootMargin?: string;

  /**
   * used to give elements top margin when scrolled to
   * using navigation else element will stick to the top of the page
   */
  scrollMarginTop?: string;
};

type UseObserveElementsByIdsState = {
  current: string | null;
};

type UseObserveElementsByIdsActions = {
  // add any actions the hook provides
};

export type UseObserveElementsByIdsValue = UseObserveElementsByIdsState &
  UseObserveElementsByIdsActions;

const DEFAULT_PROPS = {
  rootMargin: '-40% 0px 0px 0px',
} as const;

/**
 * Returns currently intersecting element when given list of
 * element ids to observe
 */
const useObserveElementsByIds = (
  props: UseObserveElementsByIdsOptions
): UseObserveElementsByIdsValue => {
  const { ids, rootMargin, scrollMarginTop } = { ...DEFAULT_PROPS, ...props };

  const elementsRef = useRef<Record<string, IntersectionObserverEntry>>({});
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        elementsRef.current = entries.reduce((map, element) => {
          map[element.target.id] = element;
          return map;
        }, elementsRef.current);

        const visibleElements: IntersectionObserverEntry[] = [];
        Object.keys(elementsRef.current).forEach((key) => {
          const element = elementsRef.current[key];
          if (element.isIntersecting) visibleElements.push(element);
        });

        const getIndexFromId = (id: string) =>
          elements.findIndex((heading) => heading.id === id);

        if (visibleElements.length === 1) {
          setCurrent(visibleElements[0].target.id);
        } else if (visibleElements.length > 1) {
          // if multiple elements are in view then set last one as current
          const sortedVisibleHeadings = visibleElements.sort(
            (a, b) => getIndexFromId(a.target.id) - getIndexFromId(b.target.id)
          );
          setCurrent(sortedVisibleHeadings[0].target.id);
        }
      },
      {
        rootMargin,
      }
    );

    const elements = ids
      .map((id) => document.querySelector(id))
      .filter((node): node is Element => node !== null);

    scrollMarginTop &&
      elements.forEach((el) =>
        el.setAttribute('style', `scroll-margin-top:${scrollMarginTop};`)
      );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [ids, rootMargin, scrollMarginTop]);

  return { current };
};

export default useObserveElementsByIds;
